<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { fly } from 'svelte/transition';
  import { responsiveStyle, parseResponsiveCSS } from '@lib/directives';
  import ScreenBlock from '../../screens/Screen/ScreenBlock.svelte';
  import type { CarouselBlock } from './types';
  import type { ElementBlock } from '../types';

  export let block: CarouselBlock;
  export let isFullscreen: boolean = false;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;

  let currentIndex = 0;
  let prevIndex = 0;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let containerEl: HTMLElement;
  let touchStartX = 0;
  let touchEndX = 0;
  let carouselHeight = 'auto';
  let itemHeights: number[] = [];

  // Track direction for transitions (inverted for natural feel)
  $: direction = currentIndex > prevIndex ? -1 : 1;
  $: if (currentIndex !== prevIndex) prevIndex = currentIndex;

  // Calculate carousel height from current item
  $: if (itemHeights[currentIndex]) {
    carouselHeight = `${itemHeights[currentIndex]}px`;
  }

  $: items = block.items || [];
  $: hasMultipleItems = items.length > 1;
  $: currentItem = items[currentIndex] || null;
  $: maxWidth = getMaxWidth();
  
  // Extract config with defaults
  $: showControls = block.config?.controls?.enabled ?? false;
  $: controlsColor = block.config?.controls?.color ?? 'white';
  $: showIndicators = block.config?.indicators?.enabled ?? true;
  $: indicatorsColor = block.config?.indicators?.color ?? 'white';
  $: autoRotate = block.config?.autoRotate ?? 0;
  $: fullWidth = block.config?.fullWidth ?? false;
  
  // Parse CSS for responsive directive
  $: responsiveCSS = block.css ? parseResponsiveCSS(block.css) : null;
  
  // Check if carousel has background
  $: hasBackground = !!block.config?.background;

  function getMaxWidth() {
    if (!block.layout?.width) return isFullscreen ? '800px' : '600px';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }

  function getCustomStyles() {
    const styles = [];
    
    // Add background styles first
    const bgStyle = getBackgroundStyle();
    if (bgStyle) styles.push(bgStyle);
    
    // Add custom CSS rules with !important to override any other styles
    if (block.css && typeof block.css === 'string') {
      const cssRules = block.css.trim();
      const rules = cssRules.split(';').filter(rule => rule.trim());
      const importantRules = rules.map(rule => {
        const trimmedRule = rule.trim();
        if (trimmedRule && !trimmedRule.includes('!important')) {
          return trimmedRule + ' !important';
        }
        return trimmedRule;
      });
      styles.push(importantRules.join('; ') + ';');
    }
    
    return styles.join(' ');
  }
  
  function getThemeColor(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
      'accent': 'var(--theme-accent-color)',
      'success': 'var(--theme-success-color)',
      'warning': 'var(--theme-warning-color)',
      'error': 'var(--theme-error-color)',
      'info': 'var(--theme-info-color)'
    };
    return themeColors[color] || color;
  }
  
  function getBackgroundStyle() {
    if (!block.config?.background) return '';
    
    const bg = block.config.background;
    
    if (bg.type === 'gradient' && bg.gradient) {
      const fromColor = getThemeColor(bg.gradient.from);
      const toColor = getThemeColor(bg.gradient.to);
      const direction = bg.gradient.direction || '135deg';
      return `background: linear-gradient(${direction}, ${fromColor}, ${toColor});`;
    }
    
    if (bg.type === 'color' && bg.color) {
      const color = getThemeColor(bg.color);
      return `background: ${color};`;
    }
    
    if (bg.type === 'image' && (bg.image?.url || bg.image?.filename)) {
      const imageUrl = bg.image.url || `/images/${bg.image.filename}`;
      return `background-image: url('${imageUrl}'); background-size: cover; background-position: center;`;
    }
    
    return '';
  }
  
  function goToSlide(index: number) {
    currentIndex = index;
    resetAutoRotate();
  }
  
  function nextSlide() {
    currentIndex = (currentIndex + 1) % items.length;
    resetAutoRotate();
  }
  
  function prevSlide() {
    currentIndex = (currentIndex - 1 + items.length) % items.length;
    resetAutoRotate();
  }
  
  function resetAutoRotate() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (autoRotate && hasMultipleItems) {
      intervalId = setInterval(() => {
        currentIndex = (currentIndex + 1) % items.length;
      }, autoRotate);
    }
  }
  
  function handleTouchStart(e: TouchEvent) {
    touchStartX = e.touches[0].clientX;
  }
  
  function handleTouchEnd(e: TouchEvent) {
    touchEndX = e.changedTouches[0].clientX;
    handleSwipe();
  }
  
  function handleSwipe() {
    const swipeThreshold = 50;
    const diff = touchStartX - touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (!hasMultipleItems) return;
    
    if (e.key === 'ArrowLeft') {
      prevSlide();
    } else if (e.key === 'ArrowRight') {
      nextSlide();
    }
  }
  
  onMount(() => {
    if (autoRotate && hasMultipleItems) {
      intervalId = setInterval(() => {
        currentIndex = (currentIndex + 1) % items.length;
      }, autoRotate);
    }
    
    // Add keyboard navigation (only in browser)
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeydown);
    }
  });
  
  onDestroy(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', handleKeydown);
    }
  });
</script>

<div
  class="carousel-block"
  class:fullscreen={isFullscreen}
  class:full-width={fullWidth}
  class:show-controls={showControls}
  class:has-background={hasBackground}
  style:--max-width={maxWidth}
  style:--height={block.layout?.height}
  style:--controls-color={getThemeColor(controlsColor)}
  style:--indicators-color={getThemeColor(indicatorsColor)}
  style:--indicators-color-dim={getThemeColor(indicatorsColor) + '80'}
  style={getCustomStyles()}
  use:responsiveStyle={responsiveCSS || {}}
  bind:this={containerEl}
  on:touchstart={handleTouchStart}
  on:touchend={handleTouchEnd}
  role="region"
  aria-label="Carousel"
  aria-roledescription="carousel"
>
  <div class="carousel-items" aria-live="polite" style="min-height: {carouselHeight}">
    <!-- Hidden measurement container -->
    <div class="carousel-measure" aria-hidden="true">
      {#each items as item, index}
        <div
          class="measure-item"
          class:measure-active={index === currentIndex}
          bind:clientHeight={itemHeights[index]}
        >
          {#if item && index === currentIndex}
            <ScreenBlock
              block={{
                ...item,
                layout: {
                  ...item.layout,
                  width: block.layout?.width || item.layout?.width
                },
                config: {
                  ...item.config,
                  fullWidth: block.config?.fullWidth || item.config?.fullWidth
                }
              }}
              index={index}
              {isFullscreen}
              {onCtaClick}
              baseDelay={0}
              delayIncrement={0}
            />
          {/if}
        </div>
      {/each}
    </div>

    <!-- Visible animated container -->
    {#key currentIndex}
      <div
        class="carousel-item active"
        in:fly={{ x: direction * 100, duration: 800, opacity: 0 }}
        out:fly={{ x: direction * -100, duration: 1800, opacity: 0 }}
        role="tabpanel"
        aria-label={`Slide ${currentIndex + 1} of ${items.length}`}
      >
        {#if currentItem}
          <ScreenBlock
            block={{
              ...currentItem,
              layout: {
                ...currentItem.layout,
                width: block.layout?.width || currentItem.layout?.width
              },
              config: {
                ...currentItem.config,
                fullWidth: block.config?.fullWidth || currentItem.config?.fullWidth
              }
            }}
            index={currentIndex}
            {isFullscreen}
            {onCtaClick}
            baseDelay={0}
            delayIncrement={0}
          />
        {/if}
      </div>
    {/key}
  </div>
  
  {#if hasMultipleItems && showIndicators}
    <div class="carousel-indicators" role="tablist">
      {#each items as _, index}
        <button
          class="indicator"
          class:active={index === currentIndex}
          on:click={() => goToSlide(index)}
          role="tab"
          aria-selected={index === currentIndex}
          aria-label={`Go to slide ${index + 1}`}
        />
      {/each}
    </div>
  {/if}
  
  {#if hasMultipleItems && showControls}
    <button 
      class="nav-button prev"
      on:click={prevSlide}
      aria-label="Previous slide"
    >
      <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
        <path d="M15 18L9 12L15 6" stroke="{getThemeColor(controlsColor)}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    <button 
      class="nav-button next"
      on:click={nextSlide}
      aria-label="Next slide"
    >
      <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
        <path d="M9 18L15 12L9 6" stroke="{getThemeColor(controlsColor)}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  {/if}
</div>

<style lang="postcss">
  .carousel-block {
    position: relative;
    width: 100%;
    max-width: var(--max-width);
    height: var(--height);
    margin: 0 auto;
    overflow: hidden;
    border-radius: var(--theme-radius, 0.375rem);
    container-type: inline-size;
  }
  
  /* Remove border radius when full-width */
  .carousel-block.full-width {
    border-radius: 0;
  }
  
  /* Remove border radius on mobile when full-width */
  @container (max-width: 600px) {
    .carousel-block.full-width {
      border-radius: 0;
    }
  }
  
  .carousel-items {
    position: relative;
    width: 100%;
    transition: min-height 0.3s ease;
  }

  /* Hidden measurement container */
  .carousel-measure {
    position: absolute;
    width: 100%;
    visibility: hidden;
    pointer-events: none;
    z-index: -1;
  }

  .measure-item {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    opacity: 0;
  }

  .measure-item.measure-active {
    position: relative;
  }

  /* Visible carousel item */
  .carousel-item {
    position: absolute;
    inset: 0;
    width: 100%;
  }
  
  /* Ensure nested blocks don't have margins */
  .carousel-item :global(.screen-block) {
    margin-bottom: 0;
    padding: 0;
  }

  /* Ensure nested hero blocks don't have extra margins */
  .carousel-item :global(.hero-block) {
    max-width: 100%;
    margin: 0;
  }
  
  /* Hero blocks inside carousel should not have their own padding */
  .carousel-item :global(.screen-block.hero) {
    padding: 0;
  }
  
  .carousel-indicators {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.5rem;
    z-index: 3;
  }
  
  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: none;
    background: var(--indicators-color-dim, rgba(255, 255, 255, 0.5));
    cursor: pointer;
    transition: all 200ms ease;
  }
  
  .indicator.active {
    background: var(--indicators-color, white);
    width: 24px;
    border-radius: 4px;
  }
  
  .nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    background: none;
    color: var(--controls-color, white);
    border: none;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0.7;
    transition: all 200ms ease;
    font-size: 20px;
    font-weight: bold;
  }

  .nav-button:hover {
    opacity: 1;
    transform: translateY(-50%) scale(1.1);
    cursor: pointer;
  }
  
  /* Show controls on hover when not explicitly shown */
  .carousel-block:not(.show-controls) .nav-button {
    opacity: 0;
  }

  .carousel-block:not(.show-controls):hover .nav-button {
    opacity: 0.7;
  }
  
  /* Always show controls when show-controls class is present */
  .carousel-block.show-controls .nav-button {
    opacity: 0.7;
  }
  
  .nav-button.prev {
    left: 1rem;
  }
  
  .nav-button.next {
    right: 1rem;
  }
  
  /* Mobile adjustments */
  @container (max-width: 600px) {
    .nav-button {
      display: none;
    }
  }
  
  /* Fullscreen adjustments */
  .carousel-block.fullscreen {
    max-width: var(--max-width);
  }
</style>